/**
 * @prop --color: Text color (also `color` can be overwritten)
 * @prop --icon-size: Icon size
 * @prop --icon-vertical-align: Icon vertical alignment
 *
 * @prop --stzh-base-color: **Global**: Text color
 * @prop --stzh-base-lead-color: **Global**: Lead text color
 */

:host {
  --icon-size: 1.25em;
  --icon-vertical-align: text-bottom;
  --color: #{$baseColor};
  --display: var(--stzh-text-display, block);

  width: 100%;
  color: var(--color);

  --grid-template-areas: var(
    --stzh-text-grid-template-areas,
    "text text text text"
  );

  --grid-template-columns: var(
    --stzh-text-grid-template-columns,
    #{$gridColumns}
  );

  @include mq($from: small) {
    --grid-template-areas: var(
      --stzh-text-grid-template-areas,
      "text text text text"
    );
  }

  @include mq($from: medium) {
    --grid-template-areas: var(
      --stzh-text-grid-template-areas,
      "text text text text text text text text"
    );

    --grid-template-columns: var(
      --stzh-text-grid-template-columns,
      #{$gridColumnsMedium}
    );
  }

  ::slotted(*:first-child) {
    margin-top: 0 !important;
  }

  ::slotted(*:last-child) {
    margin-bottom: 0 !important;
  }

  ::slotted(a),
  ::slotted(* a) {
    color: $linkColor;
    transition: color $baseTransitionAnimationSpeed;
    border-radius: $buttonBorderRadius;
    text-underline-offset: $linkTextUnderlineOffset;
    text-decoration-line: $linkTextDecorationLine;
    text-decoration-thickness: $linkTextDecorationThickness;
    text-decoration-skip-ink: $linkTextDecorationSkipInk;

    &:hover {
      color: $linkHoverColor;
      text-decoration-line: $linkHoverTextDecorationLine;

      @media (hover: none) {
        color: inherit;
      }
    }
  }

  ::slotted(b),
  ::slotted(strong),
  ::slotted(* b),
  ::slotted(* strong) {
    @include font("heavy");
  }

  ::slotted(i),
  ::slotted(em),
  ::slotted(* i),
  ::slotted(* em) {
    @include font;
  }

  &[lead]:not([lead="false"]) {
    --color: #{$baseLeadColor};
  }

  &[color="primary"] {
    --color: #{$colorPrimary70};
  }

  &[color="secondary"] {
    --color: #{$colorSecondary60};
  }

  &[color="info"] {
    --color: #{$textInfoColor};
  }

  &[color="inherit"] {
    --color: initial;
  }
}

.stzh-text {
  --stzh-icon-size: var(--icon-size);
  --stzh-icon-vertical-align: var(--icon-vertical-align);

  --stzh-list-font-size: "";
  --stzh-list-line-height: "";
  --stzh-list-letter-spacing: "";

  // default font styling without attributes
  @include fontCurve($textFontCurveDefault);
  @include gridGutter;
  display: var(--display);
  grid-template-areas: var(--grid-template-areas);
  grid-template-columns: var(--grid-template-columns);

  color: var(--color);

  &__text {
    grid-area: text;
    display: flex;
  }

  &__icon {
    flex-shrink: 0;

    &:not(:empty) {
      margin-right: 0.25em;
    }
  }

  &__text-inner {
    flex-grow: 1;
  }

  /* Lead variant */

  &--is-lead {
    @include font("heavy");
    @include fontCurve("lead", "lead");
  }

  /* Font variants */

  @each $font, $value in $fonts {
    &--font-#{$font} {
      @include font($font);
    }
  }

  /* Curve variants */

  &--curve-none {
    font-size: inherit;
  }

  @each $fontCurveName, $config in $fontCurves {
    &--curve-#{$fontCurveName} {
      @include fontCurve($fontCurveName);
    }
  }

  /* Size variants when consumer wants to overwrite predefined font curves */

  @each $size, $value in $fontSizes {
    &--size-#{$size} {
      @include fontSize($size);
    }
  }

  @each $breakpoint, $size in $breakpoints {
    @each $size, $value in $fontSizes {
      &--size-#{$breakpoint}-#{$size} {
        @include mq($from: $breakpoint) {
          @include fontSize($size);
        }
      }
    }
  }
}

.stzh-text__text-inner {
  text-overflow: var(--text-overflow, clip);
  white-space: var(--white-space, normal);
  overflow: var(--overflow, visible);
  width: var(--width, initial);
  max-width: var(--max-width, initial);
}
